// Global packages and components
import { useState, useEffect, useRef } from 'react'
import { useRouteLoaderData, useFetcher } from '@remix-run/react'
import {
  ContentEditor,
  Button,
  BranchCard,
  InlineLoader,
  AddressFinder,
} from '~/components'
import { useGetBranches } from '~/hooks'

// Types
import type { PortableTextBlock } from '@portabletext/react'
import type { RootDataProps, CustomerAddressProps } from '~/types'

type GeocodeLoaderData =
  | { ok: true; latitude: number; longitude: number }
  | { ok: false; error: string }

interface BlockBranchFinderProps {
  /**
   * Rich text editor content json
   */
  content?: PortableTextBlock[]
}

// Main export
export default function BlockBranchFinder({ content }: BlockBranchFinderProps) {
  // Hooks
  const { userAddress } = useRouteLoaderData('root') as RootDataProps
  const geocodeFetcher = useFetcher<GeocodeLoaderData>()

  // Local state
  const [address, setAddress] = useState<string>(userAddress?.line ?? '')
  const [addressData, setAddressData] = useState<CustomerAddressProps | null>(
    userAddress?.data ?? null
  )

  // Hooks
  const { branches, loading, fetchBranches } = useGetBranches()
  const wasLoadingRef = useRef(false)

  const handleFindBranch = () => {
    if (!addressData?.postcode) return

    geocodeFetcher.load(
      `/api/get-geocode?postcode=${encodeURIComponent(addressData.postcode)}`
    )
  }

  // Trigger fetchBranches once geocode result arrives.
  // Track the loading→idle transition via a ref to avoid stale closure issues.
  useEffect(() => {
    if (geocodeFetcher.state === 'loading') {
      wasLoadingRef.current = true
      return
    }

    if (
      geocodeFetcher.state === 'idle' &&
      wasLoadingRef.current &&
      geocodeFetcher.data?.ok === true
    ) {
      wasLoadingRef.current = false
      const { latitude, longitude } = geocodeFetcher.data
      fetchBranches({ latitude, longitude })
    }
  }, [geocodeFetcher.state, geocodeFetcher.data, fetchBranches])

  return (
    <div className="container">
      <div className="border-b border-gray-200 pb-10 lg:pb-14">
        <div className="mb-10">
          <ContentEditor content={content} />
        </div>
        <div className="mb-8 grid md:grid-cols-2">
          <div className="flex gap-5">
            <AddressFinder
              label="Your address"
              labelSize="sm"
              address={address}
              addressData={addressData}
              setAddress={setAddress}
              setAddressData={setAddressData}
            />
            <div className="mt-auto">
              <Button
                title="Find a branch"
                disabled={!addressData?.postcode}
                onClick={handleFindBranch}
                style="tealGreen"
              />
            </div>
          </div>
        </div>
        {loading ? (
          <InlineLoader>
            <p className="font-semibold">Fetching branches</p>
          </InlineLoader>
        ) : branches.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {branches.map((branch, key) => (
              <BranchCard
                key={key}
                title={branch.title}
                slug={branch.slug.current}
                region={branch.region}
                openingTimes={branch.openingTimes}
              />
            ))}
          </div>
        ) : geocodeFetcher.data?.ok === false ? (
          <p>Could not find location for that postcode</p>
        ) : null}
      </div>
    </div>
  )
}
